import type { CSSProperties } from "react";
import type { Message } from "./api/types";

/**
 * Words the console shows for values the server keeps in English.
 *
 * The values themselves stay as they are — they are what reports, filters and
 * the API speak — but the people reading the screen are Chinese-speaking
 * customer-service agents, and a status that says "working" is one more thing
 * to learn.
 */
export const STATUS_LABEL: Record<string, string> = {
  new: "新客",
  working: "跟进中",
  won: "成交",
  lost: "流失",
};
export const statusLabel = (s: string) => STATUS_LABEL[s] ?? s;

export const STAGE_LABEL: Record<string, string> = {
  cold: "冷",
  engaged: "互动中",
  qualified: "合格",
  intent: "有意向",
  purchased: "已购买",
};
export const stageLabel = (s: string) => STAGE_LABEL[s] ?? s;

/**
 * The funnel stages, in Meta's Leads Center words. Order matters — it is the
 * order of the dropdown and it is the funnel.
 *
 * 已转化 is not in the pickable list: it is set by recording a sale, and a
 * dropdown that lets somebody type "converted" without the money moving is how
 * a report starts lying.
 */
export const FUNNEL_LABEL: Record<string, string> = {
  pending: "待考量",
  qualified: "合格",
  converted: "已转化",
  lost: "已流失",
  disqualified: "不合格",
};
export const funnelLabel = (s: string | undefined) => FUNNEL_LABEL[s ?? "pending"] ?? s ?? "";
/** What a colleague may set by hand, in funnel order. */
export const FUNNEL_PICKABLE = ["pending", "qualified", "lost", "disqualified"] as const;
/** One line each, so nobody has to guess what a stage commits them to. */
export const FUNNEL_NOTE: Record<string, string> = {
  pending: "还没判断",
  qualified: "说过要买的话",
  lost: "问过了没下文",
  disqualified: "不是我们的客户，不再跟进也不报给 Meta",
};
/** Who decided it, for the one line under the dropdown. */
export const stageByLabel = (by: string | undefined) =>
  by === "human" ? "同事定的" : by === "system" ? "记了成交自动定的" : by === "ai" ? "系统按客户说的话判的" : "还没人定";

export const engineLabel = (e: string | undefined) =>
  e === "playbook" ? "剧本" : e === "ai" ? "AI" : "跟随默认";

/** An ad as a person can recognise it: its headline, or — Messenger sends no
 *  headline — the tail of its id, which is what Ads Manager shows beside it. */
export function adName(headline: string | undefined, id: string, network?: string): string {
  const tail = id.length > 6 ? `…${id.slice(-6)}` : id;
  if (headline) return `${headline}（${tail}）`;
  return network === "messenger" ? `Messenger 广告 ${tail}` : `广告 ${tail}`;
}

/** The same, short enough to sit on one line beside a phone number. */
export function adShort(headline: string | undefined, id: string, network?: string): string {
  if (!headline) return adName(headline, id, network);
  const chars = Array.from(headline);
  return chars.length > 14 ? chars.slice(0, 14).join("") + "…" : headline;
}

/** 012-345 6789, the way the team writes a Malaysian number; anything else as it came. */
export function phoneLabel(wa: string): string {
  if (wa.startsWith("fb:")) return "Messenger";
  const m = /^60(\d{8,10})$/.exec(wa);
  if (!m) return wa;
  const local = "0" + m[1];
  const head = local.slice(0, 3);
  const rest = local.slice(3);
  if (rest.length === 7) return `${head}-${rest.slice(0, 3)} ${rest.slice(3)}`;
  if (rest.length === 8) return `${head}-${rest.slice(0, 4)} ${rest.slice(4)}`;
  return local;
}

/** Whether a conversation lives on Messenger rather than WhatsApp. */
export function isMessenger(l: { network?: string; wa_id: string }): boolean {
  return l.network === "messenger" || l.wa_id.startsWith("fb:");
}

/** First character of a name, or of the number when there is no name. */
/**
 * A steady colour per name, so a person's face is the same every time: the team list and the account button, with
 * the prototype's sum and shade (core.js Avatar: hsl(hashHue(name) 42% 46%)).
 */
export function hueFor(s: string): string {
  let h = 0;
  for (const c of s) h = (h * 31 + c.charCodeAt(0)) % 360;
  return `hsl(${h} 42% 46%)`;
}

export function initialOf(s: string): string {
  const c = s.trim()[0];
  return c ? c.toUpperCase() : "?";
}

/**
 * A stable colour per customer. Derived from the number rather than assigned in
 * order, so the same person keeps the same colour between renders, filters and
 * sessions — an avatar that changes hue is worse than no avatar, because the
 * operator learns the colour before they read the name.
 */
export function avatarStyle(waID: string): CSSProperties {
  let h = 0;
  for (let i = 0; i < waID.length; i++) h = (h * 31 + waID.charCodeAt(i)) % 360;
  return { background: `hsl(${h} 42% 42%)` };
}

/**
 * avatarStyle's colour with every character mixed into the hue. avatarStyle's sum moves the hue by a few degrees for
 * numbers that differ only at the end, so staging's 000-000 0101…0108 all came out the same mustard on 小爱 › 花费
 * (owner, 2026-10-25). Same saturation and lightness; only the hash differs.
 */
export function avatarStyleMixed(key: string): CSSProperties {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 0x01000193);
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return { background: `hsl(${(h >>> 0) % 360} 42% 42%)` };
}

/* --------------------------------------------------------------- messages */

export type MediaKind = "image" | "video" | "audio" | "doc";

/** What one row of the transcript should read as. */
export interface Described {
  /** The words in the bubble, or the preview line in the list. */
  text: string;
  /** A quiet second line — a voice note's description under its player. */
  sub?: string;
  /** A centred grey note (a reaction, a revoke) rather than a bubble. */
  system?: boolean;
  /** Set when the row IS an attachment, for the list's little glyph. */
  icon?: MediaKind;
}

const strip = (s: string, re: RegExp) => s.replace(re, "").trim();

/**
 * Read one message the way an agent should see it.
 *
 * Three kinds of noise get in the way of that. The adapters write bracketed
 * placeholders ("[audio]", "[image]") for rows whose content is the
 * attachment; the mirror of a colleague's phone stores what it cannot classify
 * as the provider's raw JSON, which showed up in the thread as a wall of
 * signed-URL query string; and a media message's stored text is the AI's
 * description, written so the MODEL can answer about a photograph it cannot
 * see — printing it above a picture the agent is looking at tells them nothing.
 */
export function describeMessage(m: Message): Described {
  const raw = (m.text ?? "").trim();
  const kind = (m.raw_type || "").toUpperCase();
  const hasMedia = !!(m.media_key || m.media_url);

  // A sticker is its picture. When there is none to draw, all that can be said
  // is what it was.
  if (kind === "STICKER") return { text: stickerDrawn(m) ? "" : "贴图", icon: "image" };

  // A provider payload the console was never meant to print.
  if (raw.startsWith("{")) {
    try {
      const o = JSON.parse(raw) as Record<string, unknown>;
      if (typeof o.emoji === "string" && o.emoji.trim()) {
        return { text: `${o.emoji.trim()} 回应了一条消息`, system: true };
      }
      // The phone's own revoke is stored as {"revokeMessageId": …}; it read as
      // 「一条附件消息」 and nobody could see 小爱's line had been taken back.
      if (o.revoke || o.type === "revoke" || "revokeMessageId" in o) return { text: "撤回了一条消息", system: true };
      if (o.edit || o.type === "edit") return { text: "修改了一条消息", system: true };
      // Only a sticker's JSON says whether it is animated.
      if ("animated" in o) return { text: "贴图", icon: "image" };
      const flat = JSON.stringify(o);
      if (/\.(jpe?g|png|webp|gif)\b/i.test(flat)) return { text: "图片", icon: "image" };
      if (/\.(mp4|mov|3gp)\b/i.test(flat)) return { text: "视频", icon: "video" };
      if (/\.(ogg|mp3|m4a|aac|opus)\b/i.test(flat)) return { text: "语音", icon: "audio" };
      return { text: "一条附件消息", system: true };
    } catch {
      // not JSON after all; fall through and show it as it is
    }
  }
  if (/^\[edit\b/i.test(raw)) return { text: "修改了一条消息", system: true };
  if (/^\[revoke\b/i.test(raw)) return { text: "撤回了一条消息", system: true };
  if (/^\[reaction\b/i.test(raw)) return { text: "回应了一条消息", system: true };
  if (/^\[interactive\b/i.test(raw)) return { text: "点了一颗按钮", system: true };
  // WhatsApp would not hand the message over (a poll, a view-once photo…):
  // the phone has it.
  if (/^\[unsupported\b/i.test(raw)) return { text: "这条只能在手机上看", system: true };
  // The API's own words for the same (whatsapp adapter unsupportedStandIn), and for a WhatsApp notice before it
  // said 「[系统通知]」: they read as English on 今天要做's cards (QA V-09, 10/27).
  // A card or buttons sent from Messenger's Page Inbox before its words were kept (api messenger templateWords, 10/27).
  if (/^\[template\]$/i.test(raw)) return { text: "Messenger 卡片（那时没存下内容）" };
  const undelivered = /^\[(\w+) WhatsApp could not deliver to this console\b/i.exec(raw);
  if (undelivered) return { text: undelivered[1]?.toLowerCase() === "system" ? "WhatsApp 的系统通知，不是客户打的字" : "这条只能在手机上看", system: true };

  if (kind === "AUDIO" || kind === "VOICE") {
    // 小爱's and the script's voice lines are stored as 「🎤 」 and the spoken
    // sentence; a customer's as the describer's bracket. Either way the words
    // sit under the player, small — a voice row is always drawn as a voice
    // note, with or without a recording to play.
    return { text: "", sub: strip(raw, /^(\[(voice note|audio)\]|🎤)/i), icon: "audio" };
  }
  if (kind === "IMAGE" || kind === "VIDEO") {
    const icon: MediaKind = kind === "IMAGE" ? "image" : "video";
    const rest = strip(raw, /^\[(image|video)\]/i);
    if (hasMedia) {
      // What is left is our own caption; the AI's sentence about a picture the
      // agent can see is dropped.
      return { text: /^\[(image|video)\]/i.test(raw) ? "" : rest, icon };
    }
    return { text: rest || (icon === "image" ? "图片" : "视频"), icon };
  }
  // The list has no raw type to go on — a row is one string — so the same
  // bracket the adapters write is read here too. Without this the chat list
  // showed 「[image] The image shows a single red strawberry…」.
  const bracket = /^\[(image|video|audio|voice note|voice|media|sticker)\]\s*/i.exec(raw);
  if (bracket) {
    const rest = raw.slice(bracket[0].length).trim();
    const word = (bracket[1] ?? "").toLowerCase();
    if (word === "sticker") return { text: rest || "贴图", icon: "image" };
    const icon: MediaKind = word === "image" ? "image" : word === "video" ? "video" : word.startsWith("voice") || word === "audio" ? "audio" : "doc";
    return { text: rest, icon };
  }
  return { text: raw };
}

/** The provider's links live about a week; past that the file is gone. */
export const MEDIA_LINK_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The link inside a sticker row written before stickers were kept. The
 * provider's JSON — a .webp link that read as 「图片」 over nothing — is all
 * those rows have, and the link works for its week, so the ones sent in the
 * days before the fix still show and fade to 贴图 after.
 */
export function stickerLink(m: Pick<Message, "raw_type" | "text">): string {
  if ((m.raw_type || "").toUpperCase() !== "STICKER") return "";
  try {
    const o = JSON.parse((m.text ?? "").trim()) as { url?: unknown };
    return typeof o.url === "string" ? o.url : "";
  } catch {
    return "";
  }
}

/**
 * The id of the line a revoke took back: the phone's and the customer's are
 * stored as {"revokeMessageId": …}, the id a reply quotes that line by (ref).
 */
export function revokedID(m: Pick<Message, "raw_type" | "text">): string {
  if ((m.raw_type || "").toUpperCase() !== "REVOKE") return "";
  try {
    const o = JSON.parse((m.text ?? "").trim()) as { revokeMessageId?: unknown };
    return typeof o.revokeMessageId === "string" ? o.revokeMessageId : "";
  } catch {
    return "";
  }
}

/** A sticker has a picture to draw: our copy, or a provider link inside its week. */
export function stickerDrawn(m: Message): boolean {
  if (m.media_key) return true;
  if (Date.now() - new Date(m.at).getTime() > MEDIA_LINK_TTL_MS) return false;
  return !!(m.media_url || stickerLink(m));
}

const MEDIA_BRACKET = /^\[(image|video|audio|voice note|voice|media)\]/i;

/** The one-line preview a chat list shows, with its little glyph. */
export function previewOf(m: Message): Described {
  if ((m.raw_type || "").toUpperCase() === "STICKER") return { text: "贴图", icon: "image" };
  // 小爱's voice lines carry a mic rather than a bracket, and the list has no
  // raw type to go on: the list's glyph says it was a voice note, so the emoji
  // would say it twice. Only here — in a transcript a row's raw type decides,
  // and a line somebody typed starting with 🎤 keeps it.
  const said = (m.text ?? "").trim();
  if (!m.raw_type && said.startsWith("🎤")) return { text: said.slice("🎤".length).trim() || "语音", icon: "audio" };
  // A bare link — an ad's 「/goto?url=CAESZQ…」 above all — says nothing in one line (QA V-09, 10/27); the chat keeps it whole.
  if (/^(\/goto\?url=|https?:\/\/)\S+$/i.test(said)) return { text: "发了一个链接" };
  const d = describeMessage(m);
  if (d.system) return d;
  const kindWord = d.icon === "image" ? "图片" : d.icon === "video" ? "视频" : d.icon === "audio" ? "语音" : d.icon === "doc" ? "文件" : "";
  // After a media bracket come the describer's words — 「The image shows a single red strawberry…」, a voice note's
  // summary — English and long: a list says what it was, as WhatsApp does (10/24 review).
  if (MEDIA_BRACKET.test(said)) return { ...d, text: kindWord };
  const text = d.text || d.sub || kindWord;
  return { ...d, text };
}

/**
 * 来源: where a customer came from, in the team's own words. The console could
 * only say which ad somebody tapped, so everyone who came from the storefront,
 * from Messenger, from a dealer, from a friend or back for a second box was
 * one heap called 「没有广告的客人」 — 「不一定都是从我们打的广告点了 whatsapp
 * 进来的」 (owner, 2026-09-20). 广告/网店/Messenger are filled in from the
 * click, and since 2026-09-22 also 广告 from an ad's greeting and PM (wrote to
 * the number themselves); the rest only a colleague can know.
 */
export const ORIGINS: Array<{ value: string; label: string }> = [
  { value: "ad", label: "广告" },
  { value: "pm", label: "PM" },
  { value: "web", label: "网店" },
  { value: "messenger", label: "Messenger" },
  { value: "dealer", label: "代理" },
  { value: "referral", label: "转介绍" },
  { value: "repeat", label: "回头客" },
  { value: "other", label: "其他" },
];

/** 还没填 when nobody chose and the click said nothing. */
export const originLabel = (o?: string): string => ORIGINS.find((x) => x.value === o)?.label ?? "还没填";
